import { useCallback, useEffect, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 400;

type UseFilePreviewPopoverParams = {
  filesCount: number;
};

export const useFilePreviewPopover = ({ filesCount }: UseFilePreviewPopoverParams) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const open = Boolean(anchorEl);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

  const openPopover = useCallback(
    (element: HTMLElement) => {
      if (filesCount === 0) return;
      clearCloseTimeout();
      setAnchorEl(element);
    },
    [clearCloseTimeout, filesCount]
  );

  const closePopover = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
  }, [clearCloseTimeout]);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openPopover(event.currentTarget);
    },
    [openPopover]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (filesCount === 0) return;

      clearCloseTimeout();
      setAnchorEl((prev) => (prev ? null : event.currentTarget));
    },
    [clearCloseTimeout, filesCount]
  );

  const handlePopoverMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  return {
    open,
    anchorEl,
    handleMouseEnter,
    handleClick,
    scheduleClose,
    handlePopoverMouseEnter,
    clearCloseTimeout,
    closePopover,
  };
};
